/** Braced tow post with a copper-sheathed pulley, rope cleat and a world-connection rope anchor. */
import { checkEnvelope, checkScope, el, n, partGroup } from '../../svg';
import type { PieceArt, PieceRecipe, ResolvedPalette } from '../../contract';
import { beam, grainRng, ground, line, rivet } from './machinerySvg';
export interface FerryPostParams { readonly rope: 'slack' | 'taut' }
export type FerryPostRecipe = PieceRecipe<'mill.ferry_post.v1', FerryPostParams>;
export const FERRY_POST_FRAME = { w: 80, h: 160 } as const;
export const MILL_FERRY_POST: FerryPostRecipe = {
  schema: 'hush.asset-recipe.v1', id: 'mill.ferry_post.default.v1', generatorId: 'mill.ferry_post.v1',
  seed: 20071013, paletteId: 'millrace_amber', params: { rope: 'slack' },
};
export function buildFerryPost(recipe: FerryPostRecipe, p: ResolvedPalette, scope: string): PieceArt {
  checkEnvelope(recipe, MILL_FERRY_POST.generatorId); checkScope(scope);
  const { rope } = recipe.params;
  if (rope !== 'slack' && rope !== 'taut') throw new Error('mill.ferry_post.v1: rope must be slack or taut');
  const random = grainRng(recipe), parts: string[] = [], pulley = { x: 54, y: 29 }, ropeEnd = { x: 3, y: 23 };
  const footing = ground(80, 160, p)
    + el('path', { d: 'M19 155L23 137L47 135L55 155Z', fill: p.stoneBase, stroke: p.outline, 'stroke-width': 1.5 });
  const post = beam(34, 140, 34, 14, 13, p) + beam(35, 18, 65, 18, 9, p)
    + beam(37, 59, 60, 22, 6, p) + beam(34, 104, 64, 140, 8, p)
    + el('path', { d: 'M26 120H42V126H26ZM26 45H42V51H26Z', fill: p.paperShadow })
    + line(31, 67, 31 + random('grain') * 2, 113, p.woodShadow, 1.1)
    + rivet(35, 18, p) + rivet(35, 48, p);
  let wheel = el('circle', { cx: 54, cy: 29, r: 10, fill: p.woodShadow, stroke: p.outline, 'stroke-width': 2 })
    + el('circle', { cx: 54, cy: 29, r: 7, fill: 'none', stroke: p.paperBase, 'stroke-width': 2 });
  wheel += line(54, 19, 54, 39, p.paperShadow, 2) + rivet(54, 29, p, 3)
    + el('path', { d: 'M50 16H58V20H50Z', fill: p.paperBase, stroke: p.outline, 'stroke-width': 0.7 });
  const ropeD = rope === 'slack' ? 'M3 23Q22 50 50 23Q64 17 63 31L54 83' : 'M3 23H50Q64 17 63 31L54 83';
  let cord = el('path', { d: ropeD, fill: 'none', stroke: p.woodShadow, 'stroke-width': 3.4, 'stroke-linecap': 'round' })
    + el('path', { d: ropeD, fill: 'none', stroke: p.paperLight, 'stroke-width': 1.8, 'stroke-linecap': 'round' })
    + beam(44, 83, 59, 83, 3, p);
  for (let i = 0; i < 4; i++) cord += el('ellipse', { cx: 52, cy: 91 + i * 2, rx: 9 + random(`coil.${i}`), ry: 8, fill: 'none', stroke: p.paperBase, 'stroke-width': 1.4 });
  cord += el('path', { d: `M52 99Q57 110 ${n(54 + random('tail') * 4)} 117`, fill: 'none', stroke: p.paperLight, 'stroke-width': 1.6 });
  const body = partGroup(parts, 'ferry-post-footing', footing) + partGroup(parts, 'ferry-post-joinery', post)
    + partGroup(parts, 'ferry-pulley', wheel) + partGroup(parts, 'ferry-tow-rope', cord);
  return { frame: FERRY_POST_FRAME, body, anchors: { support: { x: 40, y: 160 }, pulley, rope_end: ropeEnd }, parts, openings: [] };
}
